import { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Spin } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import {
  CloseCircleOutlined,
  ArrowLeftOutlined,
  LoadingOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { checkTableName, type TableReservationNotice } from "../../../../../api/bills";
import { reservationToConfirm } from "./tableReservationNotice";
import { useReservationConfirm } from "./useReservationConfirm";
import { isCloudUnreachableError } from "../../../../../api/client";
import { useRenameBill } from "../../../../../hooks/useBills";
import { useViewport } from "../../../../../hooks/useViewport";
import { R } from "../../orderingTheme";
import {
  ModalHeader,
  GhostButton,
  PrimaryButton,
  footerButtonHeight,
} from "../../modalPrimitives";

interface RenameTableModalProps {
  open: boolean;
  billId: number | null;
  currentName: string;
  onClose: () => void;
}

const keypadRows = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
];

const MAX_NAME_LENGTH = 50;

export const RenameTableModal = ({
  open,
  billId,
  currentName,
  onClose,
}: RenameTableModalProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const renameBillMutation = useRenameBill();
  const [tableName, setTableName] = useState(currentName);
  const [error, setError] = useState("");
  // Проверката за свободно име е пропаднала. ОТДЕЛНО от `error`, защото
  // `error` заключва „Запази" — а непроверено име НЕ Е основание да се
  // откаже преименуването (заетото име се отказва от самото преименуване).
  const [checkWarning, setCheckWarning] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  // Резервацията на проверения номер (от същата проверка като „заета") —
  // при „Запази" се пита „продължаваш ли?". Пази и името, за което е.
  const [reservationCheck, setReservationCheck] = useState<{
    name: string;
    notice: TableReservationNotice;
  } | null>(null);
  const { confirmNotice, reservationConfirmModal } = useReservationConfirm();
  const submitRef = useRef<() => void>(() => {});
  const cancelRef = useRef<() => void>(() => {});
  const checkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // При всяко отваряне зареждаме текущото име като начална стойност.
  useEffect(() => {
    if (open) {
      setTableName(currentName);
      setError("");
      setCheckWarning("");
      setIsChecking(false);
      setReservationCheck(null);
    }
  }, [open, currentName]);

  const checkTableNameWithDebounce = useCallback(
    (name: string) => {
      if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);

      const trimmed = name.trim();
      setCheckWarning("");
      setReservationCheck(null);
      if (!trimmed || trimmed.toLowerCase() === currentName.trim().toLowerCase()) {
        setIsChecking(false);
        return;
      }

      setIsChecking(true);
      checkTimeoutRef.current = setTimeout(async () => {
        try {
          // Със сметката: при лична номерация се гледат масите на собственика ѝ.
          const check = await checkTableName(trimmed, billId ?? undefined);
          if (check.exists) {
            setError(t("ordering.tableAlreadyExists", { name: trimmed }));
          } else {
            setError("");
          }
          const notice = reservationToConfirm(check);
          setReservationCheck(notice ? { name: trimmed, notice } : null);
        } catch (err) {
          // Дотук тук стоеше само `console.error` и екранът МЪЛЧЕШЕ: при
          // спрял интернет въртележката се въртеше, после спираше и нищо не
          // се променяше — операторът не знаеше проверено ли е името, или не.
          // Сега го пише; „Запази" остава активно нарочно (инвариант
          // „при съмнение обслужвай"): заето име се отказва от самото
          // преименуване, а непроверено име не е причина да не се опита.
          setCheckWarning(
            isCloudUnreachableError(err)
              ? t("ordering.renameCheckOffline")
              : t("ordering.renameCheckFailed", {
                  reason: getApiErrorMessage(err, t("common.error")),
                }),
          );
        } finally {
          setIsChecking(false);
        }
      }, 500);
    },
    [t, currentName, billId],
  );

  const applyChange = useCallback(
    (next: string) => {
      const clipped = next.slice(0, MAX_NAME_LENGTH);
      setTableName(clipped);
      if (error) setError("");
      if (clipped) {
        checkTableNameWithDebounce(clipped);
      } else {
        setIsChecking(false);
        setCheckWarning("");
        if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);
      }
    },
    [error, checkTableNameWithDebounce],
  );

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        submitRef.current();
      } else if (e.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация
        e.preventDefault();
        cancelRef.current();
      } else if (
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        /^[\p{L}\p{N} .-]$/u.test(e.key)
      ) {
        setTableName((prev) => {
          const next = (prev + e.key).slice(0, MAX_NAME_LENGTH);
          if (error) setError("");
          checkTableNameWithDebounce(next);
          return next;
        });
      } else if (e.key === "Backspace") {
        setTableName((prev) => {
          const next = prev.slice(0, -1);
          if (next) {
            checkTableNameWithDebounce(next);
          } else {
            setError("");
            setCheckWarning("");
            setIsChecking(false);
            if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);
          }
          return next;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, error, checkTableNameWithDebounce]);

  const handleNumberClick = (ch: string) => applyChange(tableName + ch);
  const handleBackspace = () => applyChange(tableName.slice(0, -1));
  const handleClear = () => applyChange("");

  submitRef.current = async () => {
    if (!billId) return;
    const trimmed = tableName.trim();
    if (!trimmed) {
      setError(t("ordering.renameTableSubtitle"));
      return;
    }
    if (isChecking || error) return;
    if (trimmed === currentName.trim()) {
      onClose();
      return;
    }
    // Новият номер е зает/резервиран от резервация → „Продължаваш ли?"
    if (
      reservationCheck?.name === trimmed &&
      !(await confirmNotice(trimmed, reservationCheck.notice))
    ) {
      return;
    }
    renameBillMutation.mutate(
      { billId, name: trimmed },
      {
        onSuccess: () => {
          message.success(
            t("ordering.renameTableSuccess", { name: trimmed }),
          );
          onClose();
        },
        onError: (err: unknown) => {
          // Локализирано съобщение в самата форма — не суровият detail
          setError(getApiErrorMessage(err, t("ordering.renameTableError")));
        },
      },
    );
  };

  cancelRef.current = () => {
    onClose();
  };

  const width = isMobile ? undefined : isTablet ? 420 : 460;
  const bodyPad = isNarrow ? "16px" : "20px 24px 24px";
  const keypadGap = isNarrow ? 10 : 12;
  const btnHeight = isNarrow ? 52 : 56;
  const slotHeight = isNarrow ? 52 : 60;
  const isSaving = renameBillMutation.isPending;

  return (
    <>
    <Modal
      open={open}
      onCancel={cancelRef.current}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div style={{ background: R.surface, color: R.text }}>
        <ModalHeader
          icon={<EditOutlined />}
          title={t("ordering.renameTableTitle")}
          subtitle={t("ordering.renameTableSubtitle")}
          onClose={cancelRef.current}
          isNarrow={isNarrow}
        />

        <div
          style={{
            padding: bodyPad,
            display: "flex",
            flexDirection: "column",
            gap: isNarrow ? 14 : 16,
          }}
        >
          <div
            style={{
              position: "relative",
              height: slotHeight,
              borderRadius: 12,
              border: `1px solid ${
                error ? R.danger : tableName ? R.brand : R.border
              }`,
              background: tableName ? R.brandTint : R.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 44px",
              color: tableName ? R.brandDeep : R.textMuted,
              fontSize: 22,
              fontWeight: 700,
              fontFamily: R.mono,
              letterSpacing: 0.6,
              lineHeight: 1,
              textAlign: "center",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              transition:
                "border-color 160ms ease, background 160ms ease, color 160ms ease",
            }}
          >
            {tableName || "—"}
            {isChecking && (
              <span
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 18, color: R.brand }}
                      spin
                    />
                  }
                />
              </span>
            )}
          </div>

          {error && (
            <div
              role="alert"
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(220,38,38,0.08)",
                border: "1px solid rgba(220,38,38,0.18)",
                color: R.danger,
                fontSize: 12.5,
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {error}
            </div>
          )}

          {checkWarning && !error && (
            // Не е грешка, а недовършена проверка: другият цвят е нарочен,
            // за да не изглежда като отказ — бутонът отдолу си работи.
            <div
              role="status"
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: R.brandTint,
                border: `1px solid ${R.brandTint2}`,
                color: R.brandDeep,
                fontSize: 12.5,
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {checkWarning}
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: keypadGap,
            }}
          >
            {keypadRows.flat().map((n) => (
              <KeypadButton
                key={n}
                label={n}
                onClick={() => handleNumberClick(n)}
                height={btnHeight}
              />
            ))}

            <KeypadButton
              aria-label={t("modules.accessModal.clear") || "Clear"}
              icon={<CloseCircleOutlined style={{ fontSize: 20 }} />}
              onClick={handleClear}
              disabled={tableName.length === 0}
              height={btnHeight}
            />
            <KeypadButton
              label="0"
              onClick={() => handleNumberClick("0")}
              height={btnHeight}
            />
            <KeypadButton
              aria-label={t("modules.accessModal.backspace") || "Backspace"}
              icon={<ArrowLeftOutlined style={{ fontSize: 20 }} />}
              onClick={handleBackspace}
              disabled={tableName.length === 0}
              height={btnHeight}
            />
          </div>

          <div className="mt-1 grid grid-cols-[1fr_3fr] gap-2.5">
            <GhostButton
              label={t("common.back")}
              onClick={cancelRef.current}
              shortcut={isDesktop ? "Esc" : undefined}
              height={isNarrow ? footerButtonHeight(true) : btnHeight}
            />
            <PrimaryButton
              label={t("common.save")}
              onClick={submitRef.current}
              disabled={
                !tableName.trim() ||
                isChecking ||
                !!error ||
                tableName.trim() === currentName.trim()
              }
              loading={isSaving}
              shortcut={isDesktop ? "⏎" : undefined}
              height={isNarrow ? footerButtonHeight(true) : btnHeight}
            />
          </div>
        </div>
      </div>
    </Modal>
    {reservationConfirmModal}
    </>
  );
};

type KeypadButtonProps = {
  label?: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  height: number;
  "aria-label"?: string;
};

function KeypadButton({
  label,
  icon,
  onClick,
  disabled,
  height,
  ...rest
}: KeypadButtonProps) {
  return (
    <motion.button
      type="button"
      whileTap={disabled ? undefined : { scale: 0.97 }}
      transition={{ duration: 0.08 }}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      disabled={disabled}
      aria-label={rest["aria-label"]}
      style={{
        height,
        width: "100%",
        borderRadius: 12,
        background: R.surface,
        border: `1px solid ${R.border}`,
        color: R.ink,
        fontSize: 22,
        fontWeight: 700,
        fontFamily: "inherit",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition:
          "background 140ms ease, border-color 140ms ease, color 140ms ease",
        userSelect: "none",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = R.brandTint;
        e.currentTarget.style.borderColor = "rgba(217,119,6,0.32)";
        e.currentTarget.style.color = R.brandDeep;
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = R.surface;
        e.currentTarget.style.borderColor = R.border;
        e.currentTarget.style.color = R.ink;
      }}
    >
      {icon ?? label}
    </motion.button>
  );
}
